import { useCallback } from 'react';

import {
  type ChannelMarkupConditionApplyType,
  type ChannelMarkupConditionType,
  type ChannelMarkupEvaluationContext,
  type ChannelMarkupV2ClientInit,
  type ClientInitConfig,
  evaluateChannelMarkupRound,
} from '@luxuryescapes/contract-svc-promo/channelMarkup';
import type { components, operations } from '@luxuryescapes/contract-svc-promo/generated';

import {
  type ChannelMarkupV2Internal,
  checkChannelMarkupV2,
  createOrUpdateChannelMarkupV2,
} from '~/services/PromoService';

type Condition = {
  id: string;
  condition_type: ChannelMarkupConditionType;
  condition_apply_type: ChannelMarkupConditionApplyType;
  condition_value: string;
  specificMarkupIds: Array<string>;
};

type Markup = {
  id: string;
  markup_type: 'fixed' | 'percentage';
  markup_value: string;
  lux_plus_markup_value: string;
  channelMarkupCTAText: string;
  currency: components['schemas']['Promo Currencies'] | '';
};

type TesterValues = {
  offerId: string;
  loyaltyOfferType: string;
  region: string;
  siteLocation: string;
  subscriberTier: string;
  role: string;
  currency: string;
};

type ChannelMarkupV2Payload =
  operations['createChannelMarkupV2']['requestBody']['content']['application/json']['channelMarkup'];
type CreateOrUpdateChannelMarkupV2Response =
  operations['createChannelMarkupV2']['responses']['201']['content']['application/json'];
type PromoBrand = components['schemas']['Promo Brands'];

type TesterScenarioResult = {
  region: string;
  currency: string;
  status: ReturnType<typeof evaluateChannelMarkupRound>['status'];
  evaluation: ReturnType<typeof evaluateChannelMarkupRound>;
  conditionEvaluations: Array<{
    id: string;
    conditionType: string;
    conditionValue: string;
    passed: boolean;
    contextValue: string;
  }>;
  presentation: {
    channelMarkupCallToActionText: string | null;
    matchCount: number;
  } | null;
};

type ApplyParams = {
  isEditMode: boolean;
  createPayload: ChannelMarkupV2Payload;
  updatePayload: operations['updateChannelMarkupV2']['requestBody']['content']['application/json']['channelMarkup'];
};

type ApplyResult = {
  response: CreateOrUpdateChannelMarkupV2Response;
  channelMarkup: ChannelMarkupV2Internal | null;
  warnings: Array<string>;
};

const extractChannelMarkupFromResponse = (
  response: CreateOrUpdateChannelMarkupV2Response,
): ChannelMarkupV2Internal | null => {
  const result = response.result as ChannelMarkupV2Internal | undefined;
  return result ?? null;
};

const toNumberOrUndefined = (value: string): number | undefined => {
  if (value.trim() === '') {
    return undefined;
  }

  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
};

const trimOrFallback = (value: string | undefined, fallback: string | null = ''): string | null => {
  const trimmed = value?.trim();
  return trimmed ? trimmed : fallback;
};

const formatContextValue = (value: unknown): string => {
  if (Array.isArray(value)) {
    return value.map((item) => String(item)).join(', ');
  }

  if (value === null || value === undefined) {
    return '';
  }

  return String(value);
};

const buildConditionEvaluations = (evaluation: ReturnType<typeof evaluateChannelMarkupRound>) => {
  const evaluations = evaluation.state.candidates.flatMap((candidate) => candidate.evaluations);

  return evaluations.map((condition, index) => ({
    id: `${condition.conditionId}-${index}`,
    conditionType: condition.condition_type,
    conditionValue: condition.condition_value,
    passed: condition.status === 'match',
    contextValue: formatContextValue(condition.context.contextValue),
  }));
};

const buildRulesPayload = (
  conditions: Array<Condition>,
  markups: Array<Markup>,
): ChannelMarkupV2ClientInit['rules'] => ({
  conditions: conditions.map((condition) => ({
    conditionId: condition.id,
    specificMarkupIds: condition.specificMarkupIds.length > 0 ? condition.specificMarkupIds : null,
    conditionType: condition.condition_type,
    conditionApplyType: condition.condition_apply_type,
    conditionValue: condition.condition_value,
  })),
  markups: markups.map((markup) => ({
    markupId: markup.id,
    markupType: markup.markup_type,
    markupValue: toNumberOrUndefined(markup.markup_value),
    luxPlusMarkupValue: toNumberOrUndefined(markup.lux_plus_markup_value),
    channelMarkupCTAText: markup.channelMarkupCTAText || null,
    ...(markup.currency ? { currency: markup.currency } : {}),
  })),
});

const normalizeResponseWarnings = (response: unknown): Array<string> => {
  if (typeof response !== 'object' || response === null) {
    return [];
  }

  const candidate = response as {
    warnings?: Array<{ message?: string } | string>;
    result?: { warnings?: Array<{ message?: string } | string> };
  };

  const warnings = candidate.result?.warnings ?? candidate.warnings ?? [];
  return warnings
    .map((warning) => (typeof warning === 'string' ? warning : warning.message))
    .filter((warning): warning is string => Boolean(warning));
};

export function useChannelMarkupV2() {
  const evaluateChannelMarkup = useCallback(
    ({
      brand,
      channelMarkupId,
      conditions,
      markups,
      testerValues,
    }: {
      brand: PromoBrand;
      channelMarkupId: string;
      conditions: Array<Condition>;
      markups: Array<Markup>;
      testerValues: TesterValues;
    }): TesterScenarioResult => {
      const region = trimOrFallback(testerValues.region, 'AU') ?? 'AU';
      const currency = testerValues.currency?.trim() || 'AUD';
      const channelMarkupConfig: ClientInitConfig['channelMarkupConfig'] = {
        channelMarkupListV1: [],
        channelMarkupListV2: [
          {
            version: 'v2',
            channelMarkupId,
            rules: buildRulesPayload(conditions, markups),
          },
        ],
        globalOfferIdBlockList: [],
      };
      const context: ChannelMarkupEvaluationContext = {
        region,
        currency,
        siteLocation: trimOrFallback(testerValues.siteLocation, 'offer') ?? 'offer',
        subscriberTier: trimOrFallback(testerValues.subscriberTier, null),
        roles: (() => {
          const role = trimOrFallback(testerValues.role);
          return role ? [role] : [];
        })(),
        offer: {
          version: 'cmOfferV1',
          total: 0,
          luxPlusPrice: null,
          offerId: trimOrFallback(testerValues.offerId, 'test-offer') ?? 'test-offer',
          loyaltyOfferType: trimOrFallback(testerValues.loyaltyOfferType, 'HOTEL_FLASH') ?? 'HOTEL_FLASH',
        },
      };
      const evaluation = evaluateChannelMarkupRound({
        channelMarkupConfig,
        context,
      });

      return {
        region,
        currency,
        status: evaluation.status,
        evaluation,
        conditionEvaluations: buildConditionEvaluations(evaluation),
        presentation:
          evaluation.status === 'matched' && evaluation.matches.length > 0
            ? {
                channelMarkupCallToActionText: evaluation.matches[0].markup.channelMarkupCTAText ?? null,
                matchCount: evaluation.matches.length,
              }
            : null,
      };
    },
    [],
  );

  const checkChannelMarkup = useCallback(
    async (payload: operations['checkChannelMarkupV2']['requestBody']['content']['application/json']) => {
      const response = await checkChannelMarkupV2(payload);
      return {
        response,
        warnings: normalizeResponseWarnings(response),
      };
    },
    [],
  );

  const applyChannelMarkup = useCallback(
    async ({ isEditMode, createPayload, updatePayload }: ApplyParams): Promise<ApplyResult> => {
      const response = await createOrUpdateChannelMarkupV2(isEditMode ? updatePayload : createPayload);
      return {
        response,
        channelMarkup: extractChannelMarkupFromResponse(response),
        warnings: normalizeResponseWarnings(response),
      };
    },
    [],
  );

  return {
    evaluateChannelMarkup,
    checkChannelMarkup,
    applyChannelMarkup,
  } as const;
}
